import { t } from "@lingui/macro";
import { CircleNotchIcon, FilePdfIcon } from "@phosphor-icons/react";
import type { ResumeDto } from "@reactive-resume/dto";
import { Button } from "@reactive-resume/ui";
import { pageSizeMap } from "@reactive-resume/utils";
import type { CSSProperties } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import type { LoaderFunction } from "react-router";
import { Link, useLoaderData } from "react-router";

import { Icon } from "@/client/components/icon";
import { ThemeSwitch } from "@/client/components/theme-switch";
import { BRANDING } from "@/client/constants/branding";
import { queryClient } from "@/client/libs/query-client";
import { findResumeByUsernameSlug, usePrintResume } from "@/client/services/resume";

const openInNewTab = (url: string) => {
  const win = window.open(url, "_blank");
  if (win) win.focus();
};

export const PublicResumePage = () => {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const pageRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [pageHeight, setPageHeight] = useState<number>();

  const { printResume, loading } = usePrintResume();

  const { id, title, data: resume } = useLoaderData();
  const format = resume.metadata.page.format as keyof typeof pageSizeMap;

  useEffect(() => {
    const preview = previewRef.current;
    const page = pageRef.current;
    if (!preview || !page) return;

    const measure = () => {
      setScale(Math.min(1, preview.clientWidth / page.offsetWidth));
    };
    const observer = new ResizeObserver(measure);
    observer.observe(preview);
    measure();

    return () => {
      observer.disconnect();
    };
  }, [format]);

  const updateResumeInFrame = useCallback(() => {
    const message = { type: "SET_RESUME", payload: resume };

    setImmediate(() => {
      frameRef.current?.contentWindow?.postMessage(message, window.location.origin);
    });
  }, [frameRef.current, resume]);

  useEffect(() => {
    if (!frameRef.current) return;
    frameRef.current.addEventListener("load", updateResumeInFrame);
    return () => frameRef.current?.removeEventListener("load", updateResumeInFrame);
  }, [frameRef]);

  useEffect(() => {
    if (!frameRef.current?.contentWindow) return;

    const handleMessage = (event: MessageEvent) => {
      if (!frameRef.current?.contentWindow) return;
      if (event.origin !== window.location.origin) return;

      if (event.data.type === "PAGE_LOADED") {
        frameRef.current.width = event.data.payload.width;
        frameRef.current.height = event.data.payload.height;
        setPageHeight(frameRef.current.offsetHeight);
        frameRef.current.contentWindow.removeEventListener("message", handleMessage);
      }
    };

    frameRef.current.contentWindow.addEventListener("message", handleMessage);

    return () => {
      frameRef.current?.contentWindow?.removeEventListener("message", handleMessage);
    };
  }, [frameRef]);

  const onDownloadPdf = async () => {
    const { url } = await printResume({ id });

    openInNewTab(url);
  };

  return (
    <div className="delay-100 duration-500 animate-in fade-in slide-in-from-bottom-4 fill-mode-both">
      <Helmet>
        <title>
          {resume.basics.name} - {resume.basics.headline} | {BRANDING.name}
        </title>
        <meta name="robots" content="noindex,follow" />
      </Helmet>

      <div
        ref={previewRef}
        style={
          {
            "--page-width": `${pageSizeMap[format].width}mm`,
            "--preview-scale": scale,
            "--scaled-height": pageHeight ? `${pageHeight * scale}px` : "auto",
          } as CSSProperties
        }
        className="relative z-50 h-[var(--scaled-height)] w-full overflow-hidden rounded shadow-xl sm:mx-auto sm:mb-6 sm:mt-16 sm:h-auto sm:w-[var(--page-width)] print:m-0 print:h-auto print:w-[var(--page-width)] print:shadow-none"
      >
        <div
          ref={pageRef}
          className="origin-top-left scale-[var(--preview-scale)] sm:transform-none print:transform-none"
          style={{ width: `${pageSizeMap[format].width}mm` }}
        >
          <iframe
            ref={frameRef}
            title={title}
            src="/artboard/preview"
            style={{ width: `${pageSizeMap[format].width}mm`, overflow: "hidden" }}
          />
        </div>
      </div>

      <div className="hidden justify-center py-10 opacity-50 sm:flex print:hidden">
        <Link to="/">
          <Button size="sm" variant="ghost" className="space-x-1.5 text-xs font-normal">
            <span>{t`Built with`}</span>
            <Icon size={12} />
            <span>{t`Apply Edge`}</span>
          </Button>
        </Link>
      </div>

      <div className="fixed bottom-5 right-5 z-0 hidden sm:block print:hidden">
        <div className="flex flex-col items-center gap-y-2">
          <Button size="icon" variant="primary" className="shadow-indigo" onClick={onDownloadPdf}>
            {loading ? (
              <CircleNotchIcon size={20} className="animate-spin" />
            ) : (
              <FilePdfIcon size={20} />
            )}
          </Button>

          <ThemeSwitch />
        </div>
      </div>
    </div>
  );
};

export const publicLoader: LoaderFunction<ResumeDto> = async ({ params }) => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const username = params.username!;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const slug = params.slug!;

    return await queryClient.fetchQuery({
      queryKey: ["resume", { username, slug }],
      queryFn: () => findResumeByUsernameSlug({ username, slug }),
    });
  } catch {
    // eslint-disable-next-line @typescript-eslint/only-throw-error, lingui/no-unlocalized-strings
    throw new Response("Resume not found", { status: 404 });
  }
};
